/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * REST API を使用した認証関連 hooks
 */

import { restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

// =====================================
// User Types
// =====================================

export interface User {
  id: string;
  userId: string | null;
  organizationId: string | null;
  familyName: string | null;
  givenName: string | null;
  familyNameKana: string | null;
  givenNameKana: string | null;
  email: string;
}

/**
 * テナント情報
 * Python Backend (PostgreSQL) から返される
 */
export interface UserTenant {
  id: string;
  name: string;
  role: "owner" | "admin" | "member";
  licenses: string[];
  crmRole: "viewer" | "editor" | "admin" | null;
}

/**
 * 拡張ユーザー型
 * tenants, isSystemAdmin が存在する
 */
export type CurrentUser = User & {
  /** テナント一覧 */
  tenants?: UserTenant[];
  /** システム管理者フラグ */
  isSystemAdmin?: boolean;
};

// =====================================
// useCurrentUserRestApi - 現在のユーザー情報取得
// =====================================

export interface UseCurrentUserRestApiOptions {
  skip?: boolean;
  fetchPolicy?: "cache-first" | "network-only";
  errorPolicy?: "none" | "ignore" | "all";
  onCompleted?: () => void;
}

export interface UseCurrentUserRestApiResult {
  data: { me: User } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<User>;
}

/**
 * 現在のユーザー情報を取得するhook
 * @param {UseCurrentUserRestApiOptions} options - オプション設定
 * @return {UseCurrentUserRestApiResult} ユーザー情報、エラー、ローディング状態、refetch関数
 */
export function useCurrentUserRestApi(
  options: UseCurrentUserRestApiOptions = {},
): UseCurrentUserRestApiResult {
  const [data, setData] = useState<{ me: User } | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchCurrentUser = useCallback(async (): Promise<User> => {
    setLoading(true);
    setError(undefined);

    try {
      const responseData = await restClient.get<User>("/auth/me");
      setData({ me: responseData });
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      if (options.errorPolicy !== "ignore") {
        setError(err);
      }
      throw err;
    } finally {
      setLoading(false);
      options.onCompleted?.();
    }
  }, [options.errorPolicy, options.onCompleted]);

  useEffect(() => {
    if (!options.skip) {
      fetchCurrentUser().catch(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
    }
  }, [options.skip, fetchCurrentUser]);

  return {
    data,
    error,
    loading,
    refetch: fetchCurrentUser,
  };
}
